/* eslint-disable */
import * as Sentry from '@sentry/browser';
import { of, EmptyError } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, retry, tap } from 'rxjs/operators';
import moment from 'moment'
import envGet from '../../Support/Env/Get/EnvGet.support';
import createContainerIframe from '../../Support/Html/CreateContainerIframe';
import isIOS from '../../Support/Is/IOS/IsIOS.support';
import { buildUrl } from '../../Support/Url';

const baseUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` });
const BASE_URL_FLITCH = envGet({ key: `REACT_APP_BORAL_BASE_URL_FLITCH` });


const markViewedUrl = envGet({ key: `REACT_APP_BORAL_DOCKET_ACTIVITY_MARK_VIEWED_URL` });
const acceptUrl = envGet({ key: `REACT_APP_BORAL_DOCKET_ACCEPT_URL` });
const addNoteUrl = envGet({ key: `REACT_APP_BORAL_DOCKET_ADD_NOTE_URL` });
const deleteNoteUrl = envGet({ key: `REACT_APP_BORAL_DOCKET_DELETE_NOTE_URL` });
const getActivitiesUrl = envGet({ key: `REACT_APP_BORAL_DOCKET_ACTIVITY_URL` });

const DOCKET_PDF_CONTENT = envGet({ key: `REACT_APP_BORAL_DOCKET_PDF_CONTENT` });
const DOCKET_PDF_CONTENT_SINGLE = envGet({ key: `REACT_APP_BORAL_DOCKET_PDF_CONTENT_SINGLE` });
const DOCKET_REPORT = envGet({ key: `REACT_APP_BORAL_DOCKET_REPORT` });
const DOCKET_CSV_BY_ACCOUNT = envGet({ key: `REACT_APP_BORAL_DOCKET_CSV_BY_ACCOUNT` });


/**
 * DocketService
 * @class DocketService
 */
class DocketService {
  /**
   * The `AuthService`
   * @type {AuthService}
   * @memberof DocketService
   */
  authService;

  httpService;

  storageService;

  /**
   * Creates an instance of `DocketService`
   * @param {DocketServiceCreateDto} dto The params required to create an instance of `DocketService`
   * @memberof DocketService
   */
  constructor({ authService, httpService, storageService }) {
    this.authService = authService;
    this.httpService = httpService;
    this.storageService = storageService;
  }

  /**
   * getActivities
   * @param {Object} docketId docket id
   * @returns {Promise} promise
   */
  getActivities = ({ docketId }) => {
    const headers = this.authService.createHeaders();
    return ajax({
      headers,
      url: `${baseUrl}${getActivitiesUrl.replace(`DOCKET_ID`, docketId)}`,
    })
      .pipe(
        map((e) => e.response),
        catchError((error) => {
          Sentry.captureException(error);
          of(error);
        }),
      )
      .toPromise();
  };

  /**
   * markViewed
   * @param {Object} docketId docket id and user id
   * @returns {Promise} promise
   */
  markViewed = async ({ docketId, userId }) => {
    const headers = this.authService.createHeaders();
    const body = {
      actionType: `DOCKET_VIEWED`,
      docketId,
      userId,
    };
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `POST`,
      url: `${baseUrl}${markViewedUrl}`,
    })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          of(error);
        }),
      )
      .toPromise();
  };

  /**
   * acceptDocket
   * @param {Object} docketId docket id
   * @returns {Promise} promise
   */
  acceptDocket = async ({ docketId, accepted }) => {
    const headers = this.authService.createHeaders();
    const body = {
      accepted,
    };
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `PUT`,
      url: `${baseUrl}${acceptUrl.replace(`DOCKET_ID`, docketId)}`,
    })
      .pipe(retry(1))
      .toPromise();
  };

  /**
   * addNote
   * @param {Object} docketId docket id
   * @returns {Promise} promise
   */
  addNote = async ({ docketId, noteContent }) => {
    const headers = this.authService.createHeaders();
    const body = {
      text: noteContent,
    };
    return ajax({
      body,
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `POST`,
      url: `${baseUrl}${addNoteUrl.replace(`DOCKET_ID`, docketId)}`,
    })
      .pipe(retry(1))
      .toPromise();
  };

  /**
   * deleteNote
   * @param {Object} docketId docket id
   * @returns {Promise} promise
   */
  deleteNote = async ({ docketId, noteId }) => {
    const headers = this.authService.createHeaders();
    return ajax({
      headers: {
        ...headers,
        'Content-Type': `application/json`,
      },
      method: `DELETE`,
      url: `${baseUrl}${deleteNoteUrl.replace(`DOCKET_ID`, docketId).replace(`NOTE_ID`, noteId)}`,
    })
      .pipe(retry(1))
      .toPromise();
  };

  getDocketsPDFContent = ({ account, dockets }) => {
    const headers = {
      ...this.authService.createHeaders(),
      'Content-Type': `application/json`,
    }
    const url = `${BASE_URL_FLITCH}${DOCKET_PDF_CONTENT.replace(`{ACCOUNT}`, account)}`
    return ajax({
      url,
      method: `POST`,
      headers,
      body: { dockets },
    }).pipe(
      map((e) => e.response),
      catchError((err) => EmptyError()),
    );
  };

  getDocketPDFContentSingle = async ({ account, orderCode, orderLine, date, ticketCode }) => {
    const headers = {
      ...this.authService.createHeaders(),
      'Content-Type': `application/json`,
    }
    const url = `${BASE_URL_FLITCH}${DOCKET_PDF_CONTENT_SINGLE.replace(`{ACCOUNT}`, account)}`
    const body = { date, docketNumber: ticketCode, orderCode, orderLine }
    return ajax({
      body,
      headers,
      method: `POST`,
      url,
    })
      .pipe(retry(1))
      .toPromise();
  };

  downloadReport = async ({ account, dateFrom, dateTo, generatedOn, meta }) => {
    const headers = this.authService.createHeaders();
    const params = {
      datefrom: moment(dateFrom).format(`YYYY-MM-DD HH:mm:ssZ`),
      dateto: moment(dateTo).format(`YYYY-MM-DD HH:mm:ssZ`),
      locationcode: account,
      type: meta.type,
    };
    const url = `${BASE_URL_FLITCH}${DOCKET_REPORT}`;
    let csvData = await this.httpService.get(url, { headers, params });
    if (!csvData) {
      csvData = [`No data available for ${moment(dateFrom).format(`YYYY-MM-DD`)} to ${moment(dateTo).format(`YYYY-MM-DD`)}`];
    }
    const blob = new Blob([csvData], { type: `text/csv` });

    if (isIOS()) {
      createContainerIframe({ blob });
      return;
    }

    // simple window.open(window.URL.createObjectURL(blob)) will not work in weird browsers e.g. safari so we need to add element with link to csv we created and click it
    const a = window.document.createElement(`a`);
    a.download = `${meta.fileNamePrefix}_${account}_${generatedOn}.csv`;
    a.href = window.URL.createObjectURL(blob);
    a.textContent = ``;
    window.document.body.appendChild(a).click();
    a.remove();
    return;
  };

  getDocketsDateRangeCsv = ({ account, dateTo, dateFrom, generatedOn, meta }) => {
    const headers = { ...this.authService.createHeaders(), 'Content-Type': `application/json` };

    const built = {
      dateFrom: moment(dateFrom).format(`YYYY-MM-DDTHH:mm:ss`),
      dateTo: moment(dateTo).format(`YYYY-MM-DDTHH:mm:ss`),
    };

    const url = buildUrl(`${BASE_URL_FLITCH}${DOCKET_CSV_BY_ACCOUNT.replace(`{ACCOUNT}`, account)}`, built);

    return ajax({
      headers,
      method: `POST`,
      responseType: `text`,
      url,
    }).pipe(
      tap((result) => {
        let csvData = result.response;
        if (!csvData) {
          csvData = [`No data available for ${moment(dateFrom).format(`YYYY-MM-DD`)} to ${moment(dateTo).format(`YYYY-MM-DD`)}`];
        }
        const blob = new Blob([csvData], { type: `text/csv` });

        if (isIOS()) {
          createContainerIframe({ blob });
          return;
        }
        const a = window.document.createElement(`a`);
        a.download = `${meta.fileNamePrefix}_${account}_${generatedOn}.csv`;
        a.href = window.URL.createObjectURL(blob);
        a.textContent = ``;
        window.document.body.appendChild(a).click();
        a.remove();
        return;
      }),
    );
  };

  /**
   * Get dockets csv
   * @param {any} dto The required params to get dockets csv from api
   * @returns {Observable} Obeservable
   */
  getDocketsCsv = ({ account, columns, dateTo, dateFrom, docketIds, generatedOn, keyword, order, sort, total, meta }) => {
    const headers = { ...this.authService.createHeaders(), 'Content-Type': `application/json` };

    const built = {
      dateFrom: moment(dateFrom).format(`YYYY-MM-DDTHH:mm:ss`),
      dateTo: moment(dateTo).format(`YYYY-MM-DDTHH:mm:ss`),
      order,
      sort,
      total,
      type: meta.type,
    };

    if (keyword) {
      built.keyword = keyword;
    }

    const url = buildUrl(`${BASE_URL_FLITCH}${DOCKET_CSV_BY_ACCOUNT.replace(`{ACCOUNT}`, account)}`, built);

    return ajax({
      url,
      method: `POST`,
      headers,
      body: { docketIds, columns },
      responseType: `text`,
    }).pipe(
      tap((result) => {
        let csvData = result.response;
        if (!csvData) {
          csvData = [`No data available for ${moment(dateFrom).format(`YYYY-MM-DD`)} to ${moment(dateTo).format(`YYYY-MM-DD`)}`];
        }
        const blob = new Blob([csvData], { type: `text/csv` });

        if (isIOS()) {
          createContainerIframe({ blob });
          return;
        }
        const a = window.document.createElement(`a`);
        a.download = `${meta.fileNamePrefix}_${account}_${generatedOn}.csv`;
        a.href = window.URL.createObjectURL(blob);
        a.textContent = ``;
        window.document.body.appendChild(a).click();
        a.remove();
        return;
      }),
    );
  };
}

export default DocketService;
